import { Popover, Stack } from "@mantine/core";
import { useTimeout } from "@mantine/hooks";
import { useEffect } from "react";

import { useAgentSdk, type VoiceTask } from "@/sdk";

import { hasActiveDisclosureParticipant } from "../hasActiveDisclosureParticipant";
import styles from "./ConferencePanel.module.css";
import { ConferenceParticipantControls, PrimaryParticipantControls } from "./Participant";

export function ConferencePanel({ task }: { task: VoiceTask }) {
    const agentSdk = useAgentSdk();
    const participants = task.conferenceParticipants.filter(({ type }) => type !== "inactive");

    const primaryParticipantRejoined = task.customerLeftTheConference
        ? participants.find(({ participantName }) => participantName === task.callerId)
        : undefined;

    const filteredParticipants = primaryParticipantRejoined
        ? participants.filter(({ participantName }) => participantName !== task.callerId)
        : participants;

    const hasPending = participants.some((p) => p.type === "pending");
    // Compliance guard: while a disclosure Quick Connect is in the conference, the customer must not
    // be placed on hold (they'd stop hearing the required disclosure).
    const disclosurePlaying = hasActiveDisclosureParticipant(task.conferenceParticipants);
    const { start, clear } = useTimeout(() => void agentSdk.resetConferenceState(task), 30000);
    useEffect(() => {
        if (hasPending) {
            start();
        } else {
            clear();
        }
    }, [hasPending, start, clear]);

    return (
        <Popover zIndex="var(--mantine-priority-high)">
            <Stack
                data-testid="conference-panel"
                gap="xs"
                bg="var(--exwo-color-control-bar-bg)"
                classNames={{ root: styles.containerRoot }}
            >
                {primaryParticipantRejoined ? (
                    <ConferenceParticipantControls
                        key={primaryParticipantRejoined.participantCallId}
                        participant={primaryParticipantRejoined}
                        isPrimary
                        isPending={primaryParticipantRejoined.type === "pending"}
                        task={task}
                        agentSdk={agentSdk}
                        holdButtonDisabled={hasPending || disclosurePlaying}
                    />
                ) : (
                    <PrimaryParticipantControls holdButtonDisabled={hasPending || disclosurePlaying} />
                )}
                {filteredParticipants.map((participant) => (
                    <ConferenceParticipantControls
                        key={participant.participantCallId}
                        participant={participant}
                        isPrimary={participant.participantCallId === primaryParticipantRejoined?.participantCallId}
                        isPending={participant.type === "pending"}
                        task={task}
                        agentSdk={agentSdk}
                    />
                ))}
            </Stack>
        </Popover>
    );
}
